import * as O from 'fp-ts/lib/Option';
import {Type} from 'io-ts';
import _, {debounce, isEqual, throttle, ThrottleSettings} from 'lodash';
import {
  DependencyList,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import {globalWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import {useInitialEffect} from '../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../libs/shared/helpers/src/types/asyncStatuses';
import {buildCancelTokenSource, CancelError, CancelTokenSource} from '../../src/helpers/asyncCancelHelpers';
import {DeferredFunc, delayAsync} from '../../src/helpers/asyncHelpers';
import {isShallowEqual} from '../../src/helpers/reselectHelpers';
import {ApiType, ModelType} from '../../src/models/api/apiTypes';
import {deleteApiAsync, fetchApiAsync, postApiAsync, putApiAsync} from '../../src/services/http/http';

/** Simple toggleable state. */
export function useToggle(initialValue = false): [boolean, Handler] {
  const [value, setValue] = useState(initialValue);
  const toggle = useCallback(() => setValue((currentValue) => !currentValue), [setValue]);

  return [value, toggle];
}

/** Returns the passed value as it was after the last successful render of the component. */
export function usePrevious<T>(value: T) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function useConstantAssertion(value: unknown, onError: () => void): void {
  const valueRef = useRef(value);
  if (valueRef.current !== value) {
    onError();
  }
}

/*
 * Use Timeout.
 */

/** Returns a setTimeout-like function that clears the previous timeout when re-invoked or when the component unmounts. */
export function useTimeout(): [(handler: Handler, timeout: number) => void, () => void] {
  const handleRef = useRef<number>();

  useEffect(() => () => globalWindow.clearTimeout(handleRef.current), []);

  const safeSetTimeout = useCallback((handler: Handler, timeout: number) => {
    globalWindow.clearTimeout(handleRef.current);
    handleRef.current = globalWindow.setInstrumentedTimeout(handler, timeout);
  }, []);
  const safeClearTimeout = useCallback(() => globalWindow.clearTimeout(handleRef.current), []);

  return [safeSetTimeout, safeClearTimeout];
}

/** Returns a function that chains together multiple setTimeouts back-to-back-to-back.
 *
 *  e.g.
 *    const setTimeoutChain = useTimeoutChain();
 *    setTimeoutChain([
 *       [() => console.log("A"), 100],
 *       [() => console.log("B"), 200],
 *       [() => console.log("C"), 300]
 *    ]);
 *
 *   The above will wait 100ms, print A, then wait 200ms, print B, then wait 300ms, print C.
 */
export function useTimeoutChain() {
  const [safeSetTimeout] = useTimeout();

  return (callbackAndDelayChain: Array<[Handler, number]>) =>
    callbackAndDelayChain.reduce(
      (promiseChain, [callback, delay]) =>
        promiseChain.then(
          () =>
            new Promise((resolve, reject) => {
              safeSetTimeout(() => {
                try {
                  callback();
                } catch (error) {
                  reject(error);
                }
                resolve();
              }, delay);
            }),
        ),
      Promise.resolve(),
    );
}

/** Returns a setInterval-like function that clears the previous interval when re-invoked or when the component unmounts. */
export function useInterval(): [(handler: Handler, timeout: number) => void, () => void] {
  const handleRef = useRef<number | undefined>();

  useEffect(
    () => () => {
      if (handleRef.current) {
        globalWindow.clearInterval(handleRef.current);
      }
    },
    [],
  );

  const setIntervalFunc = useCallback((handler: Handler, timeout: number) => {
    if (handleRef.current) {
      globalWindow.clearInterval(handleRef.current);
    }

    handleRef.current = globalWindow.setInstrumentedInterval(handler, timeout);
  }, []);
  const clearIntervalFunc = () => {
    globalWindow.clearInterval(handleRef.current);
    handleRef.current = undefined;
  };
  return [setIntervalFunc, clearIntervalFunc];
}

function useDeferred<T extends (...args: any[]) => any>(
  func: T,
  makeDeferredFunc: (currentFunc: T) => DeferredFunc<T>,
  dependencies: DependencyList,
): DeferredFunc<T> {
  // Always maintain a reference to the latest version of the function.
  const funcRef = useRef(func);
  funcRef.current = func;

  // Create a function that will always call the current implemention from the ref.
  const currentFunc = ((...args: any[]) => funcRef.current(...args)) as T;

  // Initialize the more permanent refs. The deferred function + the previous dependencies.
  const deferredFuncRef = useLazyRef(() => makeDeferredFunc(currentFunc));
  const prevDependenciesRef = useRef(dependencies);

  // If the dependencies change, re-create the deferred function.
  if (!isShallowEqual(prevDependenciesRef.current, dependencies)) {
    deferredFuncRef.current = makeDeferredFunc(currentFunc);
    prevDependenciesRef.current = dependencies;
  }

  // Return the current version of the deferred function.
  return deferredFuncRef.current;
}

/** Debounce the provided function for the specified amount of time. */
export function useDebounce<T extends (...args: any[]) => any>(func: T, wait?: number) {
  const debouncedFunc = useDeferred(func, (currentFunc) => debounce(currentFunc, wait), [wait]);

  useEffect(() => () => debouncedFunc.cancel(), [debouncedFunc]);

  return debouncedFunc;
}

/** Debounce the provided value for the specified amount of time. */
export function useDebouncedValue<T>(value: T, wait?: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);
  const debouncedSetDebouncedValue = useDebounce(setDebouncedValue, wait);

  useEffect(
    () => {
      debouncedSetDebouncedValue(value);
    },
    [debouncedSetDebouncedValue, value], // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

export function useThrottle<T extends (...args: any[]) => any>(
  func: T,
  wait?: number,
  options?: ThrottleSettings,
) {
  // Don't reset the throttle for a new options object instance with the same values.
  const optionsRef = useRef(options);
  if (!isEqual(optionsRef.current, options)) {
    optionsRef.current = options;
  }

  const throttledFunc = useDeferred(func, (currentFunc) => throttle(currentFunc, wait, optionsRef.current), [
    wait,
    optionsRef.current,
  ]);

  useEffect(() => () => throttledFunc.cancel(), [throttledFunc]);

  return throttledFunc;
}

/** Provide a ref indicating whether the current component was unmounted. */
export function useWasUnmounted() {
  const wasUnmountedRef = useRef(false);
  useInitialEffect(() => () => {
    wasUnmountedRef.current = true;
  });

  return {wasUnmountedRef};
}

/** Wrap onRequestClose to not go through if the component has already been unmounted. */
export function useOnRequestClose<T extends (...args: any[]) => any>(func: T | undefined): T {
  const {wasUnmountedRef} = useWasUnmounted();

  // Wrap the provided function to only call it if we're still mounted.
  const onRequestClose = useCallback(
    (...args: any[]) => {
      if (wasUnmountedRef.current || !func) {
        return;
      }

      func(...args);
    },
    [wasUnmountedRef, func],
  );

  return onRequestClose as T;
}

/** Generate a function that builds a fresh cancel token each time, cancelling the previous one. */
export function useBuildCancelToken() {
  const cancelTokenSourceRef = useLazyRef(() => buildCancelTokenSource());

  const cancelCancelToken = useCallback(() => {
    // Cancel the existing token.
    cancelTokenSourceRef.current.cancel();
  }, [cancelTokenSourceRef]);

  const buildCancelToken = useCallback(() => {
    // Cancel the existing token.
    cancelCancelToken();

    // Create a new cancel token.
    cancelTokenSourceRef.current = buildCancelTokenSource();

    // Return the new token.
    return cancelTokenSourceRef.current.token;
  }, [cancelTokenSourceRef, cancelCancelToken]);

  // Cancel on unmount.
  useEffect(() => () => cancelCancelToken(), [cancelTokenSourceRef, cancelCancelToken]);

  return {buildCancelToken, cancelCancelToken};
}

/** Generate a cancel token that will be triggered whenever the component is unmounted. */
// TODO: Use useBuildCancelToken internally.
export function useUnmountCancelToken() {
  const cancelTokenSourceRef = useRef<CancelTokenSource>();

  // Make the token source on the first run.
  const cancelTokenSource = cancelTokenSourceRef.current || buildCancelTokenSource();
  cancelTokenSourceRef.current = cancelTokenSource;

  // Trigger on unmount.
  useEffect(() => () => cancelTokenSource.cancel(), [cancelTokenSource]);

  return cancelTokenSource.token;
}

/**
 * Forces a function component to be re-evaluated.
 * Mostly useful when used together with refs for more control compared to useState alone.
 */
export function useForceUpdate() {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  return forceUpdate as Handler;
}

export function useIsFirstRender() {
  const isFirstRender = useRef(true);
  useEffect(() => {
    isFirstRender.current = false;
  }, []);
  return isFirstRender.current;
}

const missingRefValue = Symbol('missingRefValue');

/** Flavor of useRef with lazy initialization. */
export function useLazyRef<T>(factory: () => T) {
  // Create the ref with a unique symbol as the initial value.
  const ref = useRef<T | typeof missingRefValue>(missingRefValue);

  // If it wasn't initialized, do it now.
  if (ref.current === missingRefValue) {
    ref.current = factory();
  }

  // Return the ref.
  return ref as MutableRefObject<T>;
}

/** Returns a stable constant that will be initialized on the first render. */
export function useConstant<T>(factory: () => T) {
  const ref = useLazyRef(factory);
  return ref.current;
}

/** Similar to useMemo, but deep compares the result for more stability. */
export function useDeepMemo<T>(factory: () => T, dependencies: DependencyList | undefined) {
  const value = useMemo(factory, dependencies);
  const lastValueRef = useRef<O.Option<T>>(O.none);

  // Deep compare the values to check if something has changed.
  const lastValue = lastValueRef.current;
  const result = O.isSome(lastValue) && isEqual(value, lastValue.value) ? lastValue.value : value;

  // If the result has changed, update the ref for next time.
  useEffect(() => {
    lastValueRef.current = O.some(result);
  }, [result]);

  return result;
}

/** Returns the keys that changed between component renders. For debugging only. */
export function useChangedKeys<T extends object>(object: T) {
  const previousObject = usePrevious(object);
  if (!previousObject) {
    return {};
  }

  return _(object)
    .mapValues((value, key) => {
      const previousValue = previousObject[key as keyof typeof previousObject];
      return {
        hasSameIdentity: value === previousValue,
        isEqual: isEqual(value, previousValue),
        previousValue,
        value,
      };
    })
    .pickBy((comparisonResult) => !comparisonResult.hasSameIdentity)
    .value();
}

interface UseApiPublicOptions<RTYPE extends Type<any>> {
  apiType: ApiType<RTYPE>;
  debounceDelay?: number;
  onStateChange(state: UseApiState<ModelType<RTYPE>>): void;
}

interface UseApiOptions<RTYPE extends Type<any>> extends UseApiPublicOptions<RTYPE> {
  method: 'get' | 'post' | 'put' | 'delete';
}

export type UseApiState<RESULT> = (
  | {type: 'loaded'; result: RESULT; error?: undefined}
  | {type: 'loading'; result?: undefined; error?: undefined}
  | {type: 'failed'; error: unknown; result?: undefined}
) & {asyncStatus: AsyncStatusesEnum};

/** @deprecated: Use useDebounce and fetchApiAsync instead. */
export function useFetchApi<RTYPE extends Type<any>>(options: UseApiPublicOptions<RTYPE>) {
  return useApi({method: 'get', ...options});
}

/** @deprecated: Use useDebounce and postApiAsync instead. */
export function usePostApi<RTYPE extends Type<any>>(options: UseApiPublicOptions<RTYPE>) {
  return useApi({method: 'post', ...options});
}

/** @deprecated: Use useDebounce and putApiAsync instead. */
export function usePutApi<RTYPE extends Type<any>>(options: UseApiPublicOptions<RTYPE>) {
  return useApi({method: 'put', ...options});
}

/** @deprecated: Use useDebounce and deleteApiAsync instead. */
export function useDeleteApi<RTYPE extends Type<any>>(options: UseApiPublicOptions<RTYPE>) {
  return useApi({method: 'delete', ...options});
}

/* Makes it easier to perform http calls within a React component, with support for debouncing and cancelling */
function useApi<RTYPE extends Type<any>>(options: UseApiOptions<RTYPE>) {
  const {apiType, method, debounceDelay, onStateChange} = options;

  const lastCancelTokenSource = useRef<CancelTokenSource>();

  function cancel() {
    if (lastCancelTokenSource.current) {
      lastCancelTokenSource.current.cancel();
    }
  }

  useInitialEffect(() => cancel);

  function apiCall(path: string, body?: unknown, contentType?: string) {
    cancel();
    onStateChange({type: 'loading', asyncStatus: AsyncStatusesEnum.LOADING});

    const cancelTokenSource = buildCancelTokenSource();
    lastCancelTokenSource.current = cancelTokenSource;

    const cancelToken = cancelTokenSource.token;

    const httpOptions = {
      cancelToken,
      headers: (contentType && {'content-type': contentType}) || undefined,
    };

    const delayPromise = debounceDelay ? delayAsync(debounceDelay, cancelToken) : Promise.resolve();

    return delayPromise
      .then(() => {
        switch (method) {
          case 'post':
            return postApiAsync(apiType, path, body, httpOptions);

          case 'put':
            return putApiAsync(apiType, path, body, httpOptions);

          case 'delete':
            return deleteApiAsync(apiType, path, body, httpOptions);

          default:
            return fetchApiAsync(apiType, path, httpOptions);
        }
      })
      .then((result) => {
        onStateChange({type: 'loaded', result, asyncStatus: AsyncStatusesEnum.LOADED});
        return result;
      })
      .catch((error) => {
        if (error instanceof CancelError) {
          return undefined;
        }

        onStateChange({type: 'failed', error, asyncStatus: AsyncStatusesEnum.FAILED});
        return undefined;
      });
  }

  return apiCall;
}

/** Returns a function that remembers its last arguments until after the next completed render. */
export function useLastCallEffect<A extends any[]>(
  effectCallback: (...args: A) => void,
): (...args: A) => void {
  const lastArgsRef = useRef<A>();

  useEffect(() => {
    if (!lastArgsRef.current) {
      return;
    }

    const lastArgs = lastArgsRef.current;
    lastArgsRef.current = undefined;

    effectCallback(...lastArgs);
  });

  return useCallback((...args) => {
    lastArgsRef.current = args;
  }, []);
}
